//@flow
import React from 'react';
import { compose } from 'recompose';
import { Map } from 'immutable';
import { type TFunction } from 'react-i18next';
import { type ThemeType } from 'react-css-themr';
import type { RouterHistory, Match } from 'react-router-dom';
import ReactTable from 'react-table';
import DefaultTableCell from 'common/components/common/DefaultTableCell';

import FpPlanViewTable from './FpPlanViewTable';
import queryString from 'query-string';

import theme from './FpPlanData.scss';
import { NavTabs } from 'common/components/core/tabs';
import { Box, Columns, Column, Button as NeoButton } from '@locus-taxy/neo-ui';
import { RouteNotFound, withRouter } from 'common/components/core/router';
import { Route, Switch, Redirect } from 'react-router-dom';
import Loader from 'common/components/core/loader/Loader';
import FpPlanViewPagination from './FpPlanViewPagination';
import { FilterCell } from './FilterCell';
import { type FpPjPlan } from 'common/models/internal/FpPjPlan';
import type { VisitIdOutletType } from 'common/entity/pjpMaster/planView/FpPlanDetailsContainer';
import { type BeatVisit } from 'common/models/internal/BeatVisit';
import { type FpVisitDays } from 'common/models/internal/FpOutletVisit';
import { type FpVisitAddressModelType } from 'common/models/flow/FpVisitAddress';
import DateTimeUtils, {
    DateStyle,
    TimeStyle
} from 'common/utils/DateTimeUtils';

const FpPlanDataTabs = Object.freeze({
    allocated: 'allocated',
    unallocated: 'unallocated'
});

const FieldProPlanDataTabs = [
    {
        title: 'fpPlanView.allocated',
        tabId: FpPlanDataTabs.allocated,
        path: `${FpPlanDataTabs.allocated}`
    },
    {
        title: 'fpPlanView.unallocated',
        tabId: FpPlanDataTabs.unallocated,
        path: `${FpPlanDataTabs.unallocated}`
    }
];

const SelectedViewType = Object.freeze({
    Visit: 'visit',
    Beat: 'beat',
    Unallocated: 'unallocated'
});

type FpPlanDataPagination = {
    pageSize: number,
    totalItems: number,
    pageSizeOptions: Array<number>,
    totalPages: number,
    selectedPage: number,
    selectedPageIndex: number
};

type Props = {
    visitIdMap: Map<string, VisitIdOutletType>,
    plan: FpPjPlan,
    pjpId: string,
    clientId: string,
    theme: ThemeType,
    match: Match,
    history: RouterHistory,
    t: TFunction
};

type VisitDataType = {
    id: string,
    beatId: string,
    visitId: string,
    visitSequence: number,
    outletVisitId: string,
    startTime: any,
    visitTimeDistancePairDistance: number,
    visitTimeDistancePairDuration: number,
    completedTime: any,
    totalTravelDistance: number,
    totalTravelDuration: number,
    salesman: string,
    dayOfWeek: string,
    week: string,
    outletData: Object,
    lat: number,
    lng: number,
    salesCategory: string
};

type BeatDataType = {
    beatId: string,
    beatName: string,
    categories: string,
    beatDay: string,
    beatWeek: string,
    visitCount: number,
    serviceabilityRatio: number,
    totalTravelDistance: number,
    totalTravelDuration: number,
    totalTransactionDuration: number,
    totalDuration: number,
    salesmanId: string,
    hasFrozenVisits: boolean,
    isEdited: boolean,
    lastEditedOn: string
};
type UnallocatedVisitDataType = {
    visitId: string,
    sourceOutletId: string,
    category: string,
    visitType: number,
    startTime: string,
    endTime: string,
    transactionDuration: number,
    address: FpVisitAddressModelType,
    revenue: {
        amount: ?number,
        currency: ?string,
        symbol: ?string
    },
    possibleDays: Array<FpVisitDays>
};
type State = {
    pjPlan: FpPjPlan,
    selectedTab: $Values<typeof FpPlanDataTabs>,
    selectedView: string,
    visitData: Array<VisitDataType>,
    visitViewPagination: FpPlanDataPagination,
    beatData: Array<BeatDataType>,
    beatViewPagination: FpPlanDataPagination,
    unallocatedVisitData: Array<BeatVisit>,
    unallocatedViewPagination: FpPlanDataPagination,
    paginationDetails: Map<
        string,
        FpPlanDataPagination | VisitDataType | BeatDataType
    >
};

const WEEK_DAY_SORT_PRIORITY_MAP = Object.freeze({
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7
});

class FpPlanData extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const { plan, salesbeatPlan, visitIdMap } = props;
        const visitData = this.getVisitColumns(salesbeatPlan.beats, visitIdMap);
        const visitViewPagination = this.getDefaultPaginationDetails(
            visitData.length
        );
        const beatData = this.getBeatColumns(plan.salesbeatPlan.beats);
        const beatViewPagination = this.getDefaultPaginationDetails(
            beatData.size
        );
        const unallocatedVisitData = this.getUnallocatedVisitData(
            plan.salesbeatPlan,
            visitIdMap
        );
        const unallocatedViewPagination = this.getDefaultPaginationDetails(
            unallocatedVisitData.size
        );
        const paginationDetails = Map({
            beat: beatViewPagination,
            visit: visitViewPagination,
            unallocated: unallocatedViewPagination
        });
        this.state = {
            pjPlan: props.plan,
            selectedTab: FpPlanDataTabs.allocated,
            selectedView: SelectedViewType.Beat,
            visitData,
            visitViewPagination,
            beatData,
            beatViewPagination,
            unallocatedVisitData,
            unallocatedViewPagination,
            paginationDetails
        };
    }

    componentDidMount() {
        const { clientId, location } = this.props;
        if (clientId) {
            const { pathname } = location;
            this.setState({
                selectedView: this.getSelectedView(pathname),
                selectedTab: this.getSelectedTab(pathname)
            });
        }
    }

    getSelectedTab = (pathname: string) => {
        const path = pathname.split('/');
        if (path.includes(FpPlanDataTabs.allocated)) {
            return FpPlanDataTabs.allocated;
        } else if (path.includes(FpPlanDataTabs.unallocated)) {
            return FpPlanDataTabs.unallocated;
        } else return FpPlanDataTabs.allocated;
    };

    getSelectedView = (pathname: string) => {
        const path = pathname.split('/');
        if (path.includes(SelectedViewType.Visit)) {
            return SelectedViewType.Visit;
        } else if (path.includes(SelectedViewType.Beat)) {
            return SelectedViewType.Beat;
        } else if (path.includes(SelectedViewType.Unallocated)) {
            return SelectedViewType.Unallocated;
        } else {
            return SelectedViewType.Visit;
        }
    };

    HEADER_HEIGHT: number = 42;

    rtTable = React.createRef<ReactTable>();

    updateData = (
        selectedView,
        paginationDetails,
        updatedViewPaginationDetails
    ) => {
        const updatedPaginationDetails = paginationDetails.set(
            selectedView,
            updatedViewPaginationDetails
        );
        this.setState({
            paginationDetails: updatedPaginationDetails
        });
    };

    onPageChange = (nextPageIndex: number) => {
        const { paginationDetails, selectedView } = this.state;
        const selectedPagination = paginationDetails.get(selectedView);
        const updatedViewPaginatedDetails = {
            pageSize: selectedPagination.pageSize,
            totalItems: selectedPagination.totalItems,
            totalPages: selectedPagination.totalPages,
            selectedPage: nextPageIndex + 1,
            selectedPageIndex: nextPageIndex,
            pageSizeOptions: selectedPagination.pageSizeOptions
        };

        this.updateData(
            selectedView,
            paginationDetails,
            updatedViewPaginatedDetails
        );
    };

    onPageSizeChange = (newPageSize: number) => {
        const { paginationDetails, selectedView } = this.state;
        const selectedPagination = paginationDetails.get(selectedView);
        const { totalItems, pageSizeOptions } = selectedPagination;
        const totalPages = Math.ceil(totalItems / newPageSize);
        const updatedViewPaginatedDetails = {
            pageSize: newPageSize,
            totalItems,
            totalPages,
            selectedPage: 1,
            selectedPageIndex: 0,
            pageSizeOptions
        };

        this.updateData(
            selectedView,
            paginationDetails,
            updatedViewPaginatedDetails
        );
    };

    getPaginatedData = (
        paginationDetails: FpPlanDataPagination,
        visitData: Array<
            BeatDataType | VisitDataType | UnallocatedVisitDataType
        >
    ) => {
        const {
            pageSize,
            selectedPageIndex,
            totalItems,
            totalPages
        } = paginationDetails;
        const startIndex = pageSize * selectedPageIndex;
        const endIndex =
            selectedPageIndex + 1 === totalPages
                ? totalItems
                : pageSize * (selectedPageIndex + 1);
        const paginatedData = visitData.slice(startIndex, endIndex);
        return paginatedData;
    };

    getDefaultPaginationDetails = totalVisitItems => {
        const totalPages = Math.ceil(totalVisitItems / 10);
        return {
            pageSize: 10,
            totalItems: totalVisitItems,
            totalPages,
            selectedPage: 1,
            selectedPageIndex: 0,
            pageSizeOptions: [5, 10, 15]
        };
    };

    handleTabChange = (index: number) => {
        const { match } = this.props;
        const { path, tabId } = FieldProPlanDataTabs[index];

        const pathname = `/client/${match.params.clientId}/pjp/${match.params.pjpId}/fpPlan/planData/${path}`;
        this.updateLocation({ pathname, extraQueryParams: {} });
        this.setState({
            selectedTab: tabId,
            selectedView: index
                ? SelectedViewType.Unallocated
                : SelectedViewType.Beat
        });
    };

    updateLocation = ({
        pathname,
        extraQueryParams = {}
    }: {
        pathname?: string,
        extraQueryParams: any
    }) => {
        const {
            location: { query },
            history
        } = this.props;
        const queryParams = {
            ...query,
            ...extraQueryParams
        };
        history.push({
            pathname,
            search: queryString.stringify(queryParams)
        });
    };

    getUnallocatedVisitData = (salesbeatPlan, visitIdMap) => {
        const { unallocatedVisitIds } = salesbeatPlan;
        const unallocatedVisitColumns = unallocatedVisitIds.map(visitId => {
            const outletData = visitIdMap.get(visitId);
            return {
                visitId: outletData.id,
                sourceOutletId: outletData.sourceOutletId,
                zoneId: outletData.zoneId,
                category: outletData.category,
                visitType: outletData.visitType,
                lat: outletData.address.latLng.lat,
                lng: outletData.address.latLng.lng,
                startTime: outletData.address.timeWindow.slot.start,
                endTime: outletData.address.timeWindow.slot.end,
                transactionDuration:
                    outletData.address.timeWindow.transactionDuration,
                address: outletData.address.locationAddress,
                revenue: outletData.revenue,
                possibleDays: outletData.possibleDays,
                volume: outletData.volume
            };
        });
        return unallocatedVisitColumns;
    };

    getBeatColumns = beats => {
        const beatColumns = beats.map(beat => {
            return {
                beatId: beat.beatId,
                beatName: beat.beatId,
                categories: beat.categories.get(0),
                beatDay: beat.beatDay.dayOfWeek,
                beatWeek: beat.beatDay.week,
                visitCount: beat.summary.visitCount,
                serviceabilityRatio: beat.summary.serviceabilityRatio,
                totalTravelDistance: beat.summary.totalTravelDistance,
                totalTravelDuration: beat.summary.totalTravelDuration,
                totalTransactionDuration: beat.summary.totalTransactionDuration,
                totalDuration: beat.summary.totalDuration,
                salesmanId: beat.currentSalesmanId,
                hasFrozenVisits: beat.hasFrozenVisits,
                isEdited: beat.isEdited,
                lastEditedOn: beat.lastEditedOn,
                volume: beat.summary.totalVolume
            };
        });
        return beatColumns;
    };

    onBeatViewSelect = () => {
        const { match } = this.props;
        const { clientId, pjpId } = match.params;
        const pathname = `/client/${clientId}/pjp/${pjpId}/fpPlan/planData/allocated/${SelectedViewType.Beat}`;

        this.setState(
            {
                selectedView: SelectedViewType.Beat,
                selectedTab: FpPlanDataTabs.FpPlanDataTabs
            },
            () => {
                this.updateLocation({ pathname, extraQueryParams: {} });
            }
        );
    };

    onVisitViewSelect = () => {
        const { match } = this.props;
        const { clientId, pjpId } = match.params;
        const pathname = `/client/${clientId}/pjp/${pjpId}/fpPlan/planData/allocated/${SelectedViewType.Visit}`;
        this.setState(
            {
                selectedView: SelectedViewType.Visit,
                selectedTab: FpPlanDataTabs.FpPlanDataTabs
            },
            () => {
                this.updateLocation({ pathname, extraQueryParams: {} });
            }
        );
    };

    getVisitColumns = (beats, visitIdMap) => {
        const allVisitColumns = [];
        beats.forEach(beat => {
            const { visits } = beat;
            visits.forEach((visit, index) => {
                if (visit.outletVisitId) {
                    const outletData = visitIdMap.get(visit.outletVisitId);
                    allVisitColumns.push({
                        id: `${beat.beatId}-${visit.id}`,
                        beatId: beat.beatId,
                        beatName: beat.beatName,
                        visitId: visit.outletVisitId,
                        visitSequence: index,
                        outletVisitId: visitIdMap.get(visit.outletVisitId)
                            .sourceOutletId,
                        startTime: visit.eta.startTime,
                        visitTimeDistancePairDistance:
                            visit.visitTravel.timeDistancePair.distance,
                        visitTimeDistancePairDuration:
                            visit.visitTravel.timeDistancePair.duration,
                        completedTime: visit.eta.completedTime,
                        totalTravelDistance: visit.summary.totalTravelDistance,
                        totalTravelDuration: visit.summary.totalTravelDuration,
                        salesman: beat.currentSalesmanId,
                        dayOfWeek: beat.beatDay.dayOfWeek,
                        week: beat.beatDay.week,
                        outletData,
                        lat: visit.visitLatLng.lat,
                        lng: visit.visitLatLng.lng,
                        categories: beat.categories[0],
                        volume: visit.volume
                    });
                }
            });
        });
        return allVisitColumns;
    };

    _getAddressString = ({
        placeName,
        formattedAddress,
        locality,
        state,
        countryCode,
        pincode,
        city
    }) => {
        return [
            placeName,
            formattedAddress,
            locality,
            city,
            state,
            countryCode,
            pincode
        ]
            .filter(s => s)
            .join(', ');
    };

    _getFormattedDateTime = date =>
        DateTimeUtils.format(date, {
            dateStyle: DateStyle.MEDIUM,
            timeStyle: TimeStyle.SHORT
        });

    UnallocatedColumns = Object.freeze([
        {
            id: 'visitId',
            Header: this.props.t('fpPlanView.visitId'),
            accessor: 'visitId',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'storeId',
            Header: this.props.t('fpPlanView.storeId'),
            accessor: 'sourceOutletId',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'zone',
            Header: this.props.t('fpPlanView.zoneId'),
            accessor: 'zoneId',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'volume',
            Header: this.props.t('fpPlanView.volume'),
            accessor: 'volume',
            width: 100,
            Filter: FilterCell
        },
        {
            id: 'salesCategory',
            Header: this.props.t('fpPlanView.salesCategory'),
            accessor: 'category',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'visitType',
            Header: this.props.t('fpPlanView.visitType'),
            accessor: 'visitType',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'lat',
            Header: this.props.t('fpPlanView.latitude'),
            accessor: 'lat',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'lng',
            Header: this.props.t('fpPlanView.longitude'),
            accessor: 'lng',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'startTime',
            Header: this.props.t('fpPlanView.startTime'),
            accessor: ({ startTime }) => startTime,
            width: 180,
            Filter: FilterCell,
            sortMethod: this._dateTimeSort,
            Cell: ({ value: startTime }) =>
                this._getFormattedDateTime(startTime),
            filterMethod: (data, row) => {
                const formattedDate = this._getFormattedDateTime(
                    row.startTime
                )?.toLowerCase();
                return formattedDate.includes(data.value?.toLowerCase());
            }
        },
        {
            id: 'endTime',
            Header: this.props.t('fpPlanView.endTime'),
            accessor: ({ endTime }) => endTime,
            width: 180,
            Filter: FilterCell,
            sortMethod: this._dateTimeSort,
            Cell: ({ value: endTime }) => this._getFormattedDateTime(endTime),
            filterMethod: (data, row) => {
                const formattedDate = this._getFormattedDateTime(
                    row.endTime
                )?.toLowerCase();
                return formattedDate.includes(data.value?.toLowerCase());
            }
        },
        {
            id: 'transactionDuration',
            Header: this.props.t('fpPlanView.transactionDuration'),
            accessor: original => {
                const { UnitConverter } = this.props;
                return UnitConverter.convertSeconds({
                    roundOff: true,
                    value: original.transactionDuration
                }).valueString;
            },
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'address',
            Header: this.props.t('fpPlanView.address'),
            accessor: ({ address }) => this._getAddressString(address),
            Cell: props => {
                const { value } = props;
                return <DefaultTableCell value={value} />;
            },
            Filter: FilterCell,
            width: 230
        },
        {
            id: 'revenue',
            Header: this.props.t('fpPlanView.revenue'),
            accessor: ({ revenue }) => {
                if (
                    !revenue ||
                    (revenue.symbol === null &&
                        revenue.amount === null &&
                        revenue.currency === null)
                ) {
                    return '-';
                }
                return `${revenue.symbol || ''}${
                    revenue.amount === null ? '' : revenue.amount
                } ${revenue.currency || ''}`;
            },

            width: 180,
            Filter: FilterCell
        }
    ]);

    _naturalSort = (a, b) => {
        if (a > b) {
            return 1;
        }
        if (a < b) {
            return -1;
        }
        return 0;
    };

    _dateTimeSort = (a, b) => {
        const aTime = new Date(a).getTime();
        const bTime = new Date(b).getTime();
        return this._naturalSort(aTime, bTime);
    };

    AllocatedColumns = Object.freeze([
        {
            id: 'visitId',
            Header: this.props.t('fpPlanView.visitId'),
            accessor: 'visitId',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'beatId',
            Header: this.props.t('fpPlanView.beatId'),
            accessor: 'beatId',
            width: 180,
            Filter: FilterCell,
            sortMethod: (a, b) => {
                let [, beatA] = a.split('-');
                let [, beatB] = b.split('-');
                beatA = Number(beatA);
                beatB = Number(beatB);
                return this._naturalSort(beatA, beatB);
            }
        },
        {
            id: 'storeId',
            Header: this.props.t('fpPlanView.storeId'),
            accessor: 'outletVisitId',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'visitSequence',
            Header: this.props.t('fpPlanView.visitSequence'),
            accessor: 'visitSequence',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'volume',
            Header: this.props.t('fpPlanView.volume'),
            accessor: 'volume',
            width: 100,
            Filter: FilterCell
        },
        {
            id: 'week',
            Header: this.props.t('fpPlanView.week'),
            accessor: 'week',
            width: 180,
            Filter: FilterCell,
            sortMethod: (a, b) => {
                let weekA = a.replace('WEEK', '');
                let weekB = b.replace('WEEK', '');
                weekA = Number(weekA);
                weekB = Number(weekB);
                return this._naturalSort(weekA, weekB);
            }
        },
        {
            id: 'day',
            Header: this.props.t('fpPlanView.day'),
            accessor: 'dayOfWeek',
            width: 180,
            Filter: FilterCell,
            sortMethod: (a, b) => {
                const weekDayA = WEEK_DAY_SORT_PRIORITY_MAP[a.toLowerCase()];
                const weekDayB = WEEK_DAY_SORT_PRIORITY_MAP[b.toLowerCase()];
                return this._naturalSort(weekDayA, weekDayB);
            }
        },
        {
            id: 'salesman',
            Header: this.props.t('fpPlanView.salesmanId'),
            accessor: 'salesman',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'salesCategory',
            Header: this.props.t('fpPlanView.salesCategory'),
            accessor: 'salesCategory',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'startTime',
            Header: this.props.t('fpPlanView.startTime'),
            accessor: ({ startTime }) => startTime,
            width: 180,
            Filter: FilterCell,
            sortMethod: this._dateTimeSort,
            Cell: ({ value: startTime }) =>
                this._getFormattedDateTime(startTime),
            filterMethod: (data, row) => {
                const formattedDate = this._getFormattedDateTime(
                    row.startTime
                )?.toLowerCase();
                return formattedDate.includes(data.value?.toLowerCase());
            }
        },
        {
            id: 'endTime',
            Header: this.props.t('fpPlanView.completedTime'),
            accessor: ({ completedTime }) => completedTime,
            width: 180,
            Filter: FilterCell,
            sortMethod: this._dateTimeSort,
            Cell: ({ value: completedTime }) =>
                this._getFormattedDateTime(completedTime),
            filterMethod: (data, row) => {
                const formattedDate = this._getFormattedDateTime(
                    row.endTime
                )?.toLowerCase();
                return formattedDate.includes(data.value?.toLowerCase());
            }
        },
        {
            id: 'duration',
            Header: this.props.t('fpPlanView.duration'),
            accessor: original => {
                const { UnitConverter } = this.props;
                return UnitConverter.convertSeconds({
                    roundOff: true,
                    value:
                        original.outletData.address.timeWindow
                            .transactionDuration
                }).valueString;
            },
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'address',
            Header: this.props.t('fpPlanView.address'),
            accessor: ({ outletData: { address } }) => {
                return this._getAddressString(address.locationAddress);
            },
            Cell: props => {
                const { value } = props;
                return <DefaultTableCell value={value} />;
            },
            width: 230,
            Filter: FilterCell
        }
    ]);

    BeatDataColumns = Object.freeze([
        {
            id: 'beatId',
            Header: this.props.t('fpPlanView.beatId'),
            accessor: 'beatId',
            width: 180,
            Filter: FilterCell,
            sortMethod: (a, b) => {
                let [, beatA] = a.split('-');
                let [, beatB] = b.split('-');
                beatA = Number(beatA);
                beatB = Number(beatB);
                return this._naturalSort(beatA, beatB);
            }
        },
        {
            id: 'beatName',
            Header: this.props.t('fpPlanView.beatName'),
            accessor: 'beatName',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'categories',
            Header: this.props.t('fpPlanView.category'),
            accessor: 'categories',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'volume',
            Header: this.props.t('fpPlanView.volume'),
            accessor: 'volume',
            width: 100,
            Filter: FilterCell
        },
        {
            id: 'beatDay',
            Header: this.props.t('fpPlanView.beatDay'),
            accessor: 'beatDay',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'beatWeek',
            Header: this.props.t('fpPlanView.beatWeek'),
            accessor: 'beatWeek',
            width: 180,
            Filter: FilterCell,
            sortMethod: (a, b) => {
                let weekA = a.replace('WEEK', '');
                let weekB = b.replace('WEEK', '');
                weekA = Number(weekA);
                weekB = Number(weekB);
                return this._naturalSort(weekA, weekB);
            }
        },
        {
            id: 'visitCount',
            Header: this.props.t('fpPlanView.visitCount'),
            accessor: 'visitCount',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'serviceabilityRatio',
            Header: this.props.t('fpPlanView.serviceabilityRatio'),
            accessor: 'serviceabilityRatio',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'totalTravelDistance',
            Header: this.props.t('fpPlanView.totalTravelDistance'),
            accessor: original =>
                this.props.UnitConverter.convertMeters({
                    value: original.totalTravelDistance,
                    pluralizeUnit: true,
                    roundOff: true,
                    decimals: 2,
                    showUnitAbbr: false,
                    padLeft: true
                }).valueString,
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'totalTravelDuration',
            Header: this.props.t('fpPlanView.totalTravelDuration'),
            accessor: original => {
                const { UnitConverter } = this.props;
                return UnitConverter.convertSeconds({
                    roundOff: true,
                    value: original.totalTravelDuration
                }).valueString;
            },
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'totalTransactionDuration',
            Header: this.props.t('fpPlanView.totalTransactionDuration'),
            accessor: original => {
                const { UnitConverter } = this.props;
                return UnitConverter.convertSeconds({
                    roundOff: true,
                    value: original.totalTransactionDuration
                }).valueString;
            },
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'totalDuration',
            Header: this.props.t('fpPlanView.totalDuration'),
            accessor: original => {
                const { UnitConverter } = this.props;
                return UnitConverter.convertSeconds({
                    roundOff: true,
                    value: original.totalDuration
                }).valueString;
            },
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'salesmanId',
            Header: this.props.t('fpPlanView.salesmanId'),
            accessor: 'salesmanId',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'hasFrozenVisits',
            Header: this.props.t('fpPlanView.hasFrozenVisits'),
            accessor: 'hasFrozenVisits',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'isEdited',
            Header: this.props.t('fpPlanView.isEdited'),
            accessor: 'isEdited',
            width: 180,
            Filter: FilterCell
        },
        {
            id: 'lastEditedOn',
            Header: this.props.t('fpPlanView.lastEditedOn'),
            accessor: ({ lastEditedOn }) => lastEditedOn,
            width: 180,
            Filter: FilterCell,
            sortMethod: this._dateTimeSort,
            Cell: ({ value: lastEditedOn }) =>
                this._getFormattedDateTime(lastEditedOn),
            filterMethod: (data, row) => {
                const formattedDate = this._getFormattedDateTime(
                    row.endTime
                )?.toLowerCase();
                return formattedDate.includes(data.value?.toLowerCase());
            }
        }
    ]);

    renderAllocatedTable = () => {
        const {
            selectedView,
            beatData,
            visitData,
            paginationDetails
        } = this.state;
        if (selectedView === SelectedViewType.Visit) {
            if (visitData?.length)
                return (
                    <FpPlanViewTable
                        {...this.props}
                        key={selectedView}
                        columns={this.AllocatedColumns}
                        data={visitData}
                        paginationDetails={paginationDetails.get('visit')}
                    />
                );
        } else {
            if (beatData?.size)
                return (
                    <FpPlanViewTable
                        {...this.props}
                        key={selectedView}
                        columns={this.BeatDataColumns}
                        data={beatData.toJS()}
                        paginationDetails={paginationDetails.get('beat')}
                    />
                );
        }
        return <Loader active />;
    };

    renderUnallocatedTable = () => {
        const {
            paginationDetails,
            unallocatedVisitData,
            selectedView
        } = this.state;
        if (unallocatedVisitData) {
            return (
                <Box height="fluid">
                    <FpPlanViewTable
                        {...this.props}
                        key={selectedView}
                        columns={this.UnallocatedColumns}
                        paginationDetails={paginationDetails.get('unallocated')}
                        data={
                            unallocatedVisitData.size
                                ? unallocatedVisitData.toJS()
                                : []
                        }
                    />
                </Box>
            );
        }
        return <Loader active />;
    };

    render() {
        const { t, match, salesbeatPlan } = this.props;
        const { paginationDetails, pjPlan, selectedView } = this.state;
        if (pjPlan && salesbeatPlan) {
            return (
                <Box height="fluid">
                    <Box>
                        <Columns alignY="center" height="fluid">
                            <Column width="fluid">
                                <NavTabs
                                    t={t}
                                    theme={theme}
                                    tabs={FieldProPlanDataTabs}
                                    activeTabId={this.state.selectedTab}
                                    handleTabChange={this.handleTabChange}
                                />
                            </Column>
                            <Column width="content">
                                <Box p={2}>
                                    <FpPlanViewPagination
                                        selectedPageIndex={
                                            paginationDetails.get(selectedView)
                                                .selectedPageIndex
                                        }
                                        totalPages={
                                            paginationDetails.get(selectedView)
                                                .totalPages
                                        }
                                        onPageChange={this.onPageChange}
                                        pageSize={
                                            paginationDetails.get(selectedView)
                                                .pageSize
                                        }
                                        onPageSizeChange={this.onPageSizeChange}
                                        totalItems={
                                            paginationDetails.get(selectedView)
                                                .totalItems
                                        }
                                        pageSizeOptions={
                                            paginationDetails.get(selectedView)
                                                .pageSizeOptions
                                        }
                                    />
                                </Box>
                            </Column>
                            <Column width="content">
                                <NeoButton
                                    appearance={
                                        this.state.selectedView ===
                                        SelectedViewType.Beat
                                            ? 'solid'
                                            : 'outline'
                                    }
                                    sx={{
                                        borderTopRightRadius: '0px',
                                        borderBottomRightRadius: '0px'
                                    }}
                                    palette="primary"
                                    onClick={this.onBeatViewSelect}
                                    disabled={
                                        this.state.selectedTab ===
                                        FpPlanDataTabs.unallocated
                                    }
                                >
                                    {t('fpPlanView.beatView')}
                                </NeoButton>
                            </Column>
                            <Column width="content">
                                <NeoButton
                                    sx={{
                                        borderTopLeftRadius: '0px',
                                        borderBottomLeftRadius: '0px'
                                    }}
                                    appearance={
                                        this.state.selectedView ===
                                            SelectedViewType.Visit ||
                                        this.state.selectedTab ===
                                            FpPlanDataTabs.unallocated
                                            ? 'solid'
                                            : 'outline'
                                    }
                                    palette="primary"
                                    onClick={this.onVisitViewSelect}
                                    disabled={
                                        this.state.selectedTab ===
                                        FpPlanDataTabs.unallocated
                                    }
                                >
                                    {t('fpPlanView.visitView')}
                                </NeoButton>
                            </Column>
                        </Columns>
                    </Box>

                    <Switch>
                        <Route
                            path={`${match.path}/allocated`}
                            component={this.renderAllocatedTable}
                        />
                        <Route
                            path={`${match.path}/unallocated`}
                            component={this.renderUnallocatedTable}
                        />
                        <Redirect
                            exact
                            from={match.path}
                            to={`${match.path}/allocated`}
                        />
                        <RouteNotFound />
                    </Switch>
                </Box>
            );
        }
        return <Loader active />;
    }
}

export default compose(withRouter)(FpPlanData);
